// Copyright (c) 2025 Digital Asset (Switzerland) GmbH and/or its affiliates. All rights reserved.
// SPDX-License-Identifier: Apache-2.0
import React from 'react';
import { InfoOutlined } from '@mui/icons-material';
import { Box, IconButton, Popover, Typography } from '@mui/material';

export type Status = 'Withdrawn' | 'Finalized';

interface StateCellProps {
  state: Status;
  reason?: string;
  popover?: React.ReactNode;
}

const stateColor: Record<Status, string> = {
  'Withdrawn': 'warning.main',
  'Finalized': 'success.main',
};
export const StateCell: React.FC<StateCellProps> = ({ state, popover }) => {
  const [anchorEl, setAnchorEl] = React.useState<HTMLButtonElement | null>(null);
  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);
  const id = open ? 'simple-popover' : undefined;
  return (
    <Box sx={{ display: 'flex', alignItems: 'center' }}>
      <Typography
        sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}
        color={stateColor[state]}
        variant="body2"
      >
        {state}
      </Typography>
      {popover && (
        <IconButton onClick={handleClick} size="small">
          <InfoOutlined sx={{ color: stateColor[state] }} fontSize="small" />
        </IconButton>
      )}
      {popover && (
        <Popover
          id={id}
          open={open}
          anchorEl={anchorEl}
          onClose={handleClose}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'left',
          }}
        >
          {popover}
        </Popover>
      )}
    </Box>
  );
};
